import { useEffect, useRef } from "react";

/**
 * useAlarm – plays a short repeating beep while `active` is true.
 * Uses Web Audio API so we don't need any audio asset file.
 */
export function useAlarm(active) {
  const ctxRef = useRef(null);
  const intervalRef = useRef(null);
  const startedRef = useRef(false);

  useEffect(() => {
    function unlock() {
      if (!ctxRef.current) {
        try {
          const AudioCtx = window.AudioContext || window.webkitAudioContext;
          if (AudioCtx) ctxRef.current = new AudioCtx();
        } catch { /* ignore */ }
      } else if (ctxRef.current.state === "suspended") {
        ctxRef.current.resume?.();
      }
      startedRef.current = true;
    }
    window.addEventListener("pointerdown", unlock, { once: true });
    window.addEventListener("keydown", unlock, { once: true });
    return () => {
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
    };
  }, []);

  useEffect(() => {
    if (!active) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }
    const playBeep = () => {
      const ctx = ctxRef.current;
      if (!ctx || ctx.state === "suspended") return;
      try {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "square";
        osc.frequency.setValueAtTime(880, ctx.currentTime);
        gain.gain.setValueAtTime(0.0001, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);
        osc.connect(gain).connect(ctx.destination);
        osc.start();
        osc.stop(ctx.currentTime + 0.36);
      } catch { /* ignore */ }
    };
    playBeep();
    intervalRef.current = setInterval(playBeep, 5000);
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [active]);
}
